function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function roundToHundredth(value) {
  return Math.round(value * 100) / 100;
}

const MARKER_SCALE_RATE = 1;
const MIN_MARKER_SCALE = 0.55;
const MAX_MARKER_SCALE = 1.35;

export const DEFAULT_MARKER_SIZE_RATIO = 0.022;
const DEFAULT_MARKER_SIZE = 22;
const BULK_SELECTED_MARKER_SIZE = 27;
const SELECTED_MARKER_SIZE = 32;
const BULK_SELECTED_MARKER_SIZE_MULTIPLIER = BULK_SELECTED_MARKER_SIZE / DEFAULT_MARKER_SIZE;
const SELECTED_MARKER_SIZE_MULTIPLIER = SELECTED_MARKER_SIZE / DEFAULT_MARKER_SIZE;

const MIN_DEFAULT_MARKER_SIZE = 13;
const MIN_BULK_SELECTED_MARKER_SIZE = 16;
const MIN_SELECTED_MARKER_SIZE = 20;
const MIN_CUSTOM_DEFAULT_MARKER_SIZE = 10;
const MIN_CUSTOM_SELECTED_MARKER_SIZE = 15;
const MAX_DEFAULT_MARKER_SIZE = 30;
const MAX_BULK_SELECTED_MARKER_SIZE = 36.5;
const MAX_SELECTED_MARKER_SIZE = 43;

const MIN_MARKER_BORDER_WIDTH = 1.75;
const MAX_MARKER_BORDER_WIDTH = 4.5;

// SVG ratio derived from `red-desk-icon.svg`: the "Assigned desk" marker
// has its border baked into a `<circle stroke-width="4"/>` in a 36×36
// viewBox. When CSS scales the SVG to `markerSize`, the stroke renders
// at `markerSize × 4/36`. We match that ratio in the programmatic shell
// so shell-rendered markers scale identically to the SVG marker — the
// border grows/shrinks proportionally with the marker, never "sticking".
const SHELL_BORDER_SVG_RATIO = 4 / 36;

export function getMarkerShellBorderWidth(markerSize) {
  return roundToHundredth(markerSize * SHELL_BORDER_SVG_RATIO);
}

// Hide inner content (icons, glyphs, monograms) when the marker is small
// enough that the inner glyph would hit its minimum readable size.
// Threshold of 14 px mirrors workplace's `baseContentSize <= 6` rule —
// at markerSize 14, workplace's `markerSize × 0.42 = 5.88` falls below the
// 6 px floor, so content hides. We use the marker size directly here
// because the equivalent calculation in garaje's helpers already clamps.
export const MARKER_SHELL_HIDE_CONTENT_AT_MARKER_SIZE = 14;

const ROOM_LABEL_BASE_GAP = 8;
const ROOM_LABEL_MIN_GAP = 4;
const ROOM_LABEL_MAX_GAP = 12;

export function getMarkerScaleFactor(currentZoom, baseZoom) {
  if (!Number.isFinite(currentZoom) || !Number.isFinite(baseZoom)) {
    return 1;
  }

  const zoomDiff = currentZoom - baseZoom;
  const scale = Math.pow(2, zoomDiff * MARKER_SCALE_RATE);

  return clamp(scale, MIN_MARKER_SCALE, MAX_MARKER_SCALE);
}

function getBaseLiveMapMarkerSize({ currentMapWidth, markerSizeRatio = DEFAULT_MARKER_SIZE_RATIO } = {}) {
  if (!Number.isFinite(currentMapWidth) || !Number.isFinite(markerSizeRatio)) {
    return DEFAULT_MARKER_SIZE;
  }

  return roundToHundredth(Math.max(currentMapWidth * markerSizeRatio, MIN_CUSTOM_DEFAULT_MARKER_SIZE));
}

/**
 * Returns the marker size at the reference zoom level (`baseZoom`), used as
 * the fixed render-time size for the transform-based zoom optimization.
 *
 * The live-map markers are rendered once at this size and scaled visually
 * via CSS `transform` for the current zoom — so this is what every cached
 * `<img>` is actually drawn at in the DOM.
 *
 * - In legacy mode, the unscaled default is `DEFAULT_MARKER_SIZE` (the
 *   `scaleFactor=1` value of `getLegacyLiveMapMarkerSize`).
 * - In custom (non-legacy) mode, it's `currentMapWidth × markerSizeRatio`
 *   evaluated at `baseZoom` — i.e. the raw `mapWidth` (since
 *   `getZoomScale(baseZoom, 0) === 1`) times the per-location ratio,
 *   floored at `MIN_CUSTOM_DEFAULT_MARKER_SIZE`.
 *
 * Selection / bulk multipliers are intentionally NOT applied here; they're
 * applied at transform time as `getMarkerSize(...) / baseSize`.
 */
export function getBaseMarkerSize({ legacyMarkerSizing, currentMapWidth, markerSizeRatio } = {}) {
  if (legacyMarkerSizing) {
    return DEFAULT_MARKER_SIZE;
  }
  return getBaseLiveMapMarkerSize({ currentMapWidth, markerSizeRatio });
}

export function getLiveMapMarkerSize({
  currentMapWidth,
  markerSizeRatio,
  isSelected = false,
  isBulkSelected = false,
} = {}) {
  const baseMarkerSize = getBaseLiveMapMarkerSize({ currentMapWidth, markerSizeRatio });

  if (isSelected) {
    return roundToHundredth(
      Math.max(baseMarkerSize * SELECTED_MARKER_SIZE_MULTIPLIER, MIN_CUSTOM_SELECTED_MARKER_SIZE),
    );
  }

  if (isBulkSelected) {
    return roundToHundredth(
      Math.max(baseMarkerSize * BULK_SELECTED_MARKER_SIZE_MULTIPLIER, MIN_BULK_SELECTED_MARKER_SIZE),
    );
  }

  return baseMarkerSize;
}

// `legacyMarkerSizing: true` keeps the legacy zoom-clamped behavior (ignores ratio);
// `legacyMarkerSizing: false` scales markers as `currentMapWidth × markerSizeRatio`.
// Mirrors the contract in workplace's getCustomMarkerSize.
export function getCustomMarkerSize({
  legacyMarkerSizing,
  currentMapWidth,
  markerSizeRatio,
  currentZoom,
  baseZoom,
  isSelected = false,
  isBulkSelected = false,
} = {}) {
  if (legacyMarkerSizing) {
    return getLegacyLiveMapMarkerSize({ currentZoom, baseZoom, isSelected, isBulkSelected });
  }

  return getLiveMapMarkerSize({
    currentMapWidth,
    markerSizeRatio,
    isSelected,
    isBulkSelected,
  });
}

export function getLegacyLiveMapMarkerSize({ currentZoom, baseZoom, isSelected = false, isBulkSelected = false } = {}) {
  const scaleFactor = getMarkerScaleFactor(currentZoom, baseZoom);

  if (isSelected) {
    return roundToHundredth(
      clamp(SELECTED_MARKER_SIZE * scaleFactor, MIN_SELECTED_MARKER_SIZE, MAX_SELECTED_MARKER_SIZE),
    );
  }

  if (isBulkSelected) {
    return roundToHundredth(
      clamp(BULK_SELECTED_MARKER_SIZE * scaleFactor, MIN_BULK_SELECTED_MARKER_SIZE, MAX_BULK_SELECTED_MARKER_SIZE),
    );
  }

  return roundToHundredth(clamp(DEFAULT_MARKER_SIZE * scaleFactor, MIN_DEFAULT_MARKER_SIZE, MAX_DEFAULT_MARKER_SIZE));
}

export function getMarkerBorderWidth(markerSize) {
  return roundToHundredth(clamp(markerSize * 0.11, MIN_MARKER_BORDER_WIDTH, MAX_MARKER_BORDER_WIDTH));
}

export function getMarkerInnerSize(markerSize) {
  return roundToHundredth(Math.max(markerSize - getMarkerBorderWidth(markerSize) * 2, 10));
}

export function getAvatarBorderWidth(markerSize) {
  return getMarkerBorderWidth(markerSize);
}

export function getAvatarInnerSize(markerSize) {
  return getMarkerInnerSize(markerSize);
}

export function getAvatarPhotoSize(markerSize) {
  return roundToHundredth(Math.max(markerSize - getMarkerBorderWidth(markerSize), 10));
}

export function getMarkerGlyphSize(markerSize, variant = 'feature') {
  const scale =
    {
      // Parking and POI glyphs were authored against the full circular marker canvas.
      desk: 1,
      parking: 1,
      feature: 1,
    }[variant] || 1;

  return roundToHundredth(Math.max(markerSize * scale, 8));
}

export function getAvatarMonogramFontSize(markerSize, initialsLength = 1) {
  const innerSize = getMarkerInnerSize(markerSize);
  const scale = initialsLength >= 4 ? 0.38 : initialsLength === 3 ? 0.46 : 0.54;

  return roundToHundredth(Math.max(innerSize * scale, 6));
}

export function getAvatarMonogramVerticalOffset(markerSize) {
  const fontSize = getAvatarMonogramFontSize(markerSize, 1);
  return roundToHundredth(fontSize * 0.16);
}

export function getRoomLabelGap(currentZoom, baseZoom) {
  return roundToHundredth(
    clamp(ROOM_LABEL_BASE_GAP * getMarkerScaleFactor(currentZoom, baseZoom), ROOM_LABEL_MIN_GAP, ROOM_LABEL_MAX_GAP),
  );
}
